import { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { useWizardState, hasResumableState } from './wizard/useWizardState';
import WizardLayout from './wizard/WizardLayout';
import WizardStep1Identity from './wizard/WizardStep1Identity';
import WizardStep2Branding from './wizard/WizardStep2Branding';
import WizardStep3Features from './wizard/WizardStep3Features';
import WizardStep4Content from './wizard/WizardStep4Content';
import WizardStep5Products from './wizard/WizardStep5Products';
import WizardStep6Review from './wizard/WizardStep6Review';
import { Store, Palette, ToggleLeft, FileText, Package, Rocket, RotateCcw, Play } from 'lucide-react';
import Button from '@/components/ui/Button';
import api from '@/lib/api';

const STEPS = [
  { label: 'Identity', icon: Store },
  { label: 'Branding', icon: Palette },
  { label: 'Features', icon: ToggleLeft },
  { label: 'Content', icon: FileText },
  { label: 'Products', icon: Package },
  { label: 'Review', icon: Rocket },
];

const STEP_INFO = [
  { title: "Let's set up your site", description: "Tell us about your business and choose your domain." },
  { title: "Design your brand", description: "AI will help you create a beautiful color palette and pick fonts." },
  { title: "Choose your features", description: "We'll recommend the best features based on your business type." },
  { title: "Customize your content", description: "Set up your homepage, about page, and key messaging." },
  { title: "Plan your products", description: "Define what you'll sell and let AI generate sample content." },
  { title: "Review & launch", description: "Review everything and create your site." },
];

export default function SiteWizard() {
  const user = useAuthStore((s) => s.user);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const state = useWizardState();
  const { updateState, resetState } = state;

  const [showResumeModal, setShowResumeModal] = useState(false);

  // A draft whose siteId is ALREADY PROVISIONED. The persisted draft survives
  // a successful launch, so the next visit restored 'DumBox' — a live tenant —
  // straight onto Review & launch with "Resume" as the primary button, one
  // click from "Create My Site" against an existing site (2026-09-09). When
  // the registry says the id exists, Resume is not offered at all.
  const [liveSiteForDraft, setLiveSiteForDraft] = useState<{ id: string; name: string; domain: string } | null>(null);

  // Check for resumable state on mount
  useEffect(() => {
    if (!hasResumableState()) return;
    setShowResumeModal(true);
    if (!state.siteId) return;
    let active = true;
    api
      .get<{ sites: Array<{ id: string; name: string; domain: string }> }>('/admin/platform/site-wizard/sites')
      .then((res) => {
        if (!active) return;
        const hit = (res?.sites ?? []).find((s) => s.id === state.siteId);
        if (hit) setLiveSiteForDraft(hit);
      })
      .catch(() => {
        // Registry unreachable: fall back to the ordinary prompt rather than
        // block. The worst case is the old behaviour, not a new one.
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to top when changing steps
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [state.currentStep]);

  // Wait for auth to initialize before checking permissions
  if (!isInitialized) {
    return <div className="p-8 text-center text-text-secondary">Loading...</div>;
  }

  if (!user?.isPlatformAdmin) {
    return <div className="p-8 text-center text-text-secondary">Access denied. Platform admin required.</div>;
  }

  function handleResume() {
    setShowResumeModal(false);
    // State is already loaded from zustand persist, nothing else needed
  }

  function handleStartOver() {
    resetState();
    setShowResumeModal(false);
  }

  function isCurrentStepValid(): boolean {
    switch (state.currentStep) {
      case 0:
        return !!(state.storeName && state.siteId && state.businessDescription);
      case 1:
        return !!state.palettes;
      case 2:
        return Object.keys(state.features).length > 0;
      case 3: {
        if (!state.heroTitle) return false;
        // If Cloudflare added a new zone, the user must copy each
        // nameserver to their registrar before continuing.
        const ns = state.pendingNameservers || [];
        const copied = state.copiedNameservers || [];
        return ns.every((n) => copied.includes(n));
      }
      case 4:
        return true;
      case 5:
        return true;
      default:
        return false;
    }
  }

  function handleNext() {
    if (state.currentStep >= STEPS.length - 1) return;

    const completed = state.completedSteps.includes(state.currentStep)
      ? state.completedSteps
      : [...state.completedSteps, state.currentStep];

    const nextStep = state.currentStep + 1;
    // Clear the "return to" bookmark once normal forward navigation
    // reaches or passes it — the user got back on their own.
    const clearReturn = state.returnToStep != null && nextStep >= state.returnToStep;

    updateState({
      completedSteps: completed,
      currentStep: nextStep,
      ...(clearReturn ? { returnToStep: null } : {}),
    });
  }

  function handleBack() {
    if (state.currentStep <= 0) return;
    updateState({ currentStep: state.currentStep - 1 });
  }

  function handleStepClick(step: number) {
    if (!state.completedSteps.includes(step) && step !== state.currentStep) return;
    // Jumping BACKWARD from a later step: stash the current step so the
    // user can "Return to step N" with one click. Don't overwrite an
    // existing bookmark — if they jump back twice, the original
    // bookmark is the one they want to return to.
    const isJumpingBack = step < state.currentStep;
    const shouldBookmark = isJumpingBack && state.returnToStep == null;
    updateState({
      currentStep: step,
      ...(shouldBookmark ? { returnToStep: state.currentStep } : {}),
    });
  }

  function handleReturnToStep() {
    if (state.returnToStep == null) return;
    updateState({
      currentStep: state.returnToStep,
      returnToStep: null,
    });
  }

  function renderStep() {
    const props = { state, updateState };
    switch (state.currentStep) {
      case 0: return <WizardStep1Identity {...props} />;
      case 1: return <WizardStep2Branding {...props} />;
      case 2: return <WizardStep3Features {...props} />;
      case 3: return <WizardStep4Content {...props} />;
      case 4: return <WizardStep5Products {...props} />;
      case 5: return <WizardStep6Review {...props} />;
      default: return null;
    }
  }

  function ResumeModal() {
    if (liveSiteForDraft) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6 space-y-4" data-testid="stale-draft-modal">
            <h2 className="text-lg font-display font-bold text-text-primary">
              This draft is for a site that's already live
            </h2>
            <p className="text-sm text-text-secondary">
              <strong>'{state.storeName || liveSiteForDraft.name}'</strong> was provisioned as{' '}
              <code>{liveSiteForDraft.id}</code>
              {liveSiteForDraft.domain ? <> at <code>{liveSiteForDraft.domain}</code></> : null}.
              Resuming would try to create it again, so this draft has been retired.
            </p>
            <div className="flex gap-3 pt-2">
              {liveSiteForDraft.domain ? (
                <Button
                  variant="ghost"
                  onClick={() => window.open(`https://${liveSiteForDraft.domain}/admin`, '_blank', 'noopener')}
                  className="flex-1"
                >
                  Open its admin
                </Button>
              ) : null}
              <Button variant="boutique" onClick={handleStartOver} className="flex-1">
                <RotateCcw className="w-4 h-4" />
                Start a new site
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6 space-y-4">
          <h2 className="text-lg font-display font-bold text-text-primary">
            Welcome back!
          </h2>
          <p className="text-sm text-text-secondary">
            You were working on <strong>'{state.storeName || 'your site'}'</strong>.
            Resume where you left off?
          </p>
          <div className="flex gap-3 pt-2">
            <Button variant="ghost" onClick={handleStartOver} className="flex-1">
              <RotateCcw className="w-4 h-4" />
              Start Over
            </Button>
            <Button variant="boutique" onClick={handleResume} className="flex-1">
              <Play className="w-4 h-4" />
              Resume
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <WizardLayout
      steps={STEPS}
      currentStep={state.currentStep}
      totalSteps={STEPS.length}
      stepTitle={STEP_INFO[state.currentStep].title}
      stepDescription={STEP_INFO[state.currentStep].description}
      onNext={handleNext}
      onBack={handleBack}
      onStepClick={handleStepClick}
      completedSteps={state.completedSteps}
      isNextDisabled={!isCurrentStepValid()}
      nextLabel="Continue"
      hideNext={state.currentStep === STEPS.length - 1}
      savedAt={state.savedAt}
      returnToStep={state.returnToStep}
      returnToStepLabel={state.returnToStep != null ? STEPS[state.returnToStep].label : null}
      onReturnToStep={handleReturnToStep}
    >
      {showResumeModal && <ResumeModal />}
      {renderStep()}
    </WizardLayout>
  );
}
